<template>
  <div class="chart-card">
    <div class="chart-card-header">
      <Dropdown
        :titleDropdown="selectedChart.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li
            v-for="chart in availableCharts"
            :key="chart.type"
            @click="changeChartType(chart.type)"
            class="bkps-dropdown-item"
            :title="chart.description"
          >
            <i :class="chart.icon"></i>
            {{ chart.name }}
          </li>
        </ul>
      </Dropdown>

      <Dropdown
        :titleDropdown="selectedSort.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li
            v-for="sort in availableSorts"
            :key="sort.type"
            @click="changeSortType(sort.type)"
            class="bkps-dropdown-item"
            :title="sort.description"
          >
            <i :class="sort.icon"></i>
            {{ sort.name }}
          </li>
        </ul>
      </Dropdown>
    </div>
    <div ref="targetChart" style="height: 430px; width: 100%; margin-top: 10px;"></div>
  </div>
</template>

<script>
import Highcharts from 'highcharts/es5/highcharts'
import ChartMix from '@/components/charts/ChartMix.vue'
import ChartThemeMixin from '@/components/charts/ChartThemeMixin.vue'
import Dropdown from '@/components/shares/specialTag/Dropdown.vue'

const calculatePercentage = (value, total) => (total ? (value * 100) / total : 0)

export default {
  name: 'CmFrotasCnpjChart',
  components: { Dropdown },
  mixins: [ChartMix, ChartThemeMixin],
  props: {
    graphData: { required: true, type: Array },
    graphTitle: { required: true, type: String },
    loading: { required: true, type: Boolean }
  },
  data() {
    return {
      currentType: 'bar',
      sortBy: 'value',
      chartInstance: null,
      availableCharts: [
        { type: 'pie', name: 'Pizza', icon: 'fas fa-chart-pie', description: 'Gráfico de pizza' },
        { type: 'donut', name: 'Donut', icon: 'fas fa-dot-circle', description: 'Gráfico donut' },
        { type: 'bar', name: 'Barras', icon: 'fas fa-chart-bar', description: 'Gráfico de barras' },
        { type: 'line', name: 'Linha', icon: 'fas fa-chart-line', description: 'Gráfico de linhas' }
      ],
      availableSorts: [
        { type: 'value', name: 'Valor', icon: 'fas fa-dollar-sign', description: 'Exibir por valor (R$)' },
        { type: 'count', name: 'Quantidade', icon: 'fas fa-hashtag', description: 'Exibir por quantidade' }
      ]
    }
  },
  computed: {
    selectedChart() { return this.availableCharts.find(c => c.type === this.currentType) },
    selectedSort() { return this.availableSorts.find(s => s.type === this.sortBy) }
  },
  methods: {
    changeChartType(type) { this.currentType = type; this.createChart() },
    changeSortType(type) { this.sortBy = type; this.createChart() },
    buildSeries() {
      if (!this.graphData || this.graphData.length === 0) return []

      const isCount = this.sortBy === 'count'
      const rows = this.graphData.filter(c => c.cnpj)
      const total = rows.reduce((acc, c) => acc + (isCount ? (c.count || 0) : (c.sum || 0)), 0)

      return rows
        .map(c => {
          const metric = isCount ? (c.count || 0) : (c.sum || 0)
          return {
            name: c.name || c.cnpj,
            y: calculatePercentage(metric, total),
            rawValue: (c.sum || 0) / 100,
            count: c.count || 0,
            cnpj: c.cnpj
          }
        })
        .sort((a, b) => b.y - a.y)
    },
    createChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }

      const serie = this.buildSeries()
      if (serie.length === 0) return

      const theme = this.chartTheme
      const sortBy = this.sortBy
      const yAxisTitle = sortBy === 'value' ? 'Porcentagem por Valor (%)' : 'Porcentagem por Quantidade (%)'
      let chartConfig = {}

      if (this.currentType === 'pie') {
        chartConfig = {
          chart: { type: 'pie', backgroundColor: theme.backgroundColor, height: 430, style: { fontFamily: '"Montserrat", sans-serif' } },
          plotOptions: {
            pie: {
              borderRadius: 8,
              borderWidth: 2,
              borderColor: theme.backgroundColor,
              dataLabels: {
                enabled: true,
                distance: 10,
                connectorWidth: 1,
                style: { fontWeight: '600', fontSize: '11px', color: theme.textColor, textOutline: 'none' },
                format: '{point.name}: {point.y:.1f}%'
              }
            }
          },
          series: [{ name: 'CNPJ', colorByPoint: true, data: serie }]
        }
      } else if (this.currentType === 'donut') {
        chartConfig = {
          chart: { type: 'pie', backgroundColor: theme.backgroundColor, height: 430, marginTop: 60, marginBottom: 60, style: { fontFamily: '"Montserrat", sans-serif' } },
          plotOptions: {
            pie: {
              allowPointSelect: true,
              cursor: 'pointer',
              innerSize: '60%',
              borderRadius: 8,
              borderWidth: 3,
              borderColor: theme.backgroundColor,
              center: ['50%', '50%'],
              size: '85%',
              dataLabels: {
                enabled: true,
                distance: 15,
                connectorWidth: 1,
                style: { color: theme.textColor, fontWeight: '600', fontSize: '11px', textOutline: 'none' },
                format: '{point.name}: {point.y:.1f}%'
              }
            }
          },
          series: [{ name: 'CNPJ', colorByPoint: true, data: serie }]
        }
      } else if (this.currentType === 'bar') {
        const sorted = [...serie].sort((a, b) => b.y - a.y)
        chartConfig = {
          chart: { type: 'column', backgroundColor: theme.backgroundColor, height: 430, marginBottom: 160, marginTop: 60, marginLeft: 70, marginRight: 20, style: { fontFamily: '"Montserrat", sans-serif' } },
          xAxis: { categories: sorted.map(i => i.name), labels: { rotation: -45, overflow: 'allow', style: { color: theme.textColor, fontSize: '10px' } } },
          yAxis: { title: { text: yAxisTitle, style: { color: theme.textColor } }, labels: { style: { color: theme.textColor } }, gridLineColor: theme.gridLineColor },
          legend: { enabled: false },
          plotOptions: { column: { borderRadius: 6, borderWidth: 0 } },
          series: [{ type: 'column', name: 'CNPJ', data: sorted, colorByPoint: true }]
        }
      } else if (this.currentType === 'line') {
        const sorted = [...serie].sort((a, b) => b.y - a.y)
        chartConfig = {
          chart: { type: 'line', backgroundColor: theme.backgroundColor, height: 430, marginBottom: 160, marginTop: 60, marginLeft: 70, marginRight: 20, style: { fontFamily: '"Montserrat", sans-serif' } },
          xAxis: { categories: sorted.map(i => i.name), labels: { rotation: -45, overflow: 'allow', style: { color: theme.textColor, fontSize: '10px' } } },
          yAxis: { title: { text: yAxisTitle, style: { color: theme.textColor } }, labels: { style: { color: theme.textColor } }, gridLineColor: theme.gridLineColor },
          legend: { enabled: false },
          plotOptions: { line: { marker: { enabled: true, radius: 5 }, lineWidth: 3 } },
          series: [{ type: 'line', name: 'CNPJ', data: sorted, color: theme.colors[0] }]
        }
      }

      const baseConfig = {
        colors: theme.colors,
        credits: { enabled: false },
        title: { text: this.currentTitle, align: 'center', style: { color: theme.textColor, fontSize: '18px', fontWeight: '600' } },
        tooltip: {
          outside: true,
          backgroundColor: theme.backgroundColor,
          borderColor: theme.gridLineColor,
          borderRadius: 8,
          style: { color: theme.textColor, fontSize: '12px', zIndex: 9999 },
          headerFormat: '',
          formatter: function () {
            const lines = [
              `<span style="color:${this.point.color}">${this.point.name}</span>`,
              `Porcentagem: <b>${Highcharts.numberFormat(this.y, 2, ',', '.')}%</b>`
            ]
            if (this.point.cnpj) lines.push(`CNPJ: <b>${this.point.cnpj}</b>`)
            if (typeof this.point.rawValue === 'number') lines.push(`Valor: <b>R$ ${Highcharts.numberFormat(this.point.rawValue, 2, ',', '.')}</b>`)
            if (typeof this.point.count === 'number') lines.push(`Quantidade: <b>${Highcharts.numberFormat(this.point.count, 0, ',', '.')}</b>`)
            return lines.join('<br/>')
          }
        }
      }

      this.chartInstance = Highcharts.chart(this.$refs.targetChart, { ...baseConfig, ...chartConfig })
      this.$nextTick(() => { if (this.chartInstance) this.chartInstance.reflow() })
    }
  },
  mounted() {
    this.createChart()
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
      this.chartInstance = null
    }
  },
  watch: {
    isDarkTheme: {
      handler() { this.$nextTick(() => this.createChart()) },
      immediate: false
    },
    graphData: {
      handler() { this.$nextTick(() => this.createChart()) },
      deep: true
    }
  }
}
</script>
